import React from 'react'
import { createRoot } from 'react-dom/client'
import { createInertiaApp } from '@inertiajs/react'
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material'
import Layout from '../components/Layout'
import '@fontsource/inter/300.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/700.css'
import '@fontsource/pacifico/400.css'

// KpopMap "Navi" palette (purple). The mascot accent stays orange.
const purple = '#7B5BD6'
const purpleHover = '#6A47C9'
const purpleSoft = '#EDE6FE'
const purpleDeep = '#5A37B8'
const lavenderBg = '#FAF7FF'
const naviOrange = '#F4A05A'
const cardShadow = '0 2px 12px rgba(123, 91, 214, 0.10)'

const theme = createTheme({
  palette: {
    primary: { main: purple, dark: purpleHover, light: purpleSoft, contrastText: '#fff' },
    secondary: { main: purpleSoft, dark: purpleDeep, contrastText: purpleHover },
    background: { default: lavenderBg, paper: '#ffffff' },
    text: { primary: '#1F1A2E', secondary: '#6E6786' },
    divider: 'rgba(123, 91, 214, 0.18)',
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: { fontSize: '2.5rem', fontWeight: 800, letterSpacing: '-0.02em' },
    h4: { fontSize: '1.5rem', fontWeight: 700 },
    h5: { fontSize: '1.25rem', fontWeight: 700 },
    h6: { fontSize: '1rem', fontWeight: 700 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: lavenderBg },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 999, fontWeight: 600 },
        containedPrimary: {
          boxShadow: 'none',
          '&:hover': { backgroundColor: purpleHover, boxShadow: 'none' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: { root: { padding: '12px 16px' } },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 28,
          borderRadius: 999,
          fontSize: '0.75rem',
          fontWeight: 600,
        },
        filledDefault: {
          backgroundColor: purpleSoft,
          color: purpleHover,
        },
        outlined: {
          borderColor: 'rgba(123, 91, 214, 0.35)',
          color: purpleHover,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundImage: 'none',
        },
        outlined: {
          border: '1px solid rgba(123, 91, 214, 0.15)',
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: cardShadow,
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: { boxShadow: 'none', backgroundImage: 'none' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          backgroundColor: '#fff',
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: '#1F1A2E', borderRadius: 8 },
      },
    },
  },
})

// Expose tokens for components that need raw values (e.g. map markers).
export const designTokens = {
  primary: purple,
  primaryHover: purpleHover,
  primarySoft: purpleSoft,
  primaryDeep: purpleDeep,
  bg: lavenderBg,
  naviOrange,
  cardShadow,
  // Backwards-compat aliases (kept temporarily so any stragglers still resolve).
  coral: purple,
  coralHover: purpleHover,
  peach: purpleSoft,
  peachDeep: purpleDeep,
}

interface NoLayoutComponent {
  noLayout?: boolean
}

createInertiaApp({
  resolve: (name: string) => {
    const pages = import.meta.glob('../pages/**/*.tsx', { eager: true }) as Record<string, { default: React.ComponentType }>
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      throw new Error(`Page not found: ${name}. Available: ${Object.keys(pages).join(', ')}`)
    }
    return page
  },
  setup({ el, App, props }) {
    createRoot(el).render(
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App {...props}>
          {({ Component, props: pageProps, key }) => {
            const componentMeta = Component as React.ComponentType & NoLayoutComponent
            const node = <Component key={key} {...pageProps} />
            if (componentMeta.noLayout) {
              return node
            }
            return <Layout>{node}</Layout>
          }}
        </App>
      </ThemeProvider>
    )
  },
})
